/**
 * AddDepartmentModal Component
 * Modal for adding a user to a department with role selection
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, LoadingSpinner } from '../common';
import { useAddUserDepartment, useDepartments } from '../../hooks';

export interface AddDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  existingDepartmentIds: string[];
}

export const AddDepartmentModal: React.FC<AddDepartmentModalProps> = ({
  isOpen,
  onClose,
  userId,
  existingDepartmentIds,
}) => {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [role, setRole] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  const { data: allDepartments = [], isLoading: isLoadingDepartments } = useDepartments();
  const addMutation = useAddUserDepartment();

  // Filter out departments user is already in
  const availableDepartments = allDepartments.filter(
    (dept) => !existingDepartmentIds.includes(dept.id)
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDepartmentId('');
      setRole('');
      setIsPrimary(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDepartmentId) {
      return;
    }

    try {
      await addMutation.mutateAsync({
        userId,
        data: {
          departmentId: selectedDepartmentId,
          role: role || null,
          isPrimary,
        },
      });
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleClose = () => {
    if (!addMutation.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add User to Department"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isLoadingDepartments ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : availableDepartments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-stone-500 mb-2">No available departments</p>
            <p className="text-sm text-stone-400">
              This user is already assigned to all departments.
            </p>
          </div>
        ) : (
          <>
            <Select
              label="Department"
              value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(e.target.value)}
              options={[
                { value: '', label: 'Select a department', disabled: false },
                ...availableDepartments.map((dept) => ({
                  value: dept.id,
                  label: dept.name,
                  disabled: false,
                })),
              ]}
              required
            />

            <Select
              label="Role (Optional)"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              options={[
                { value: '', label: 'No Role', disabled: false },
                { value: 'contributor', label: 'Contributor', disabled: false },
                { value: 'lead', label: 'Lead', disabled: false },
                { value: 'manager', label: 'Manager', disabled: false },
              ]}
              helperText="Assign a role-specific responsibility within this department"
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPrimary"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-600"
              />
              <label htmlFor="isPrimary" className="text-sm text-stone-700">
                Set as primary department
              </label>
            </div>

            {isPrimary && (
              <div className="bg-primary-50 border border-primary-200 rounded-md p-3">
                <p className="text-sm text-primary-800">
                  Setting this as the primary department will replace the current primary department.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={addMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!selectedDepartmentId || addMutation.isPending}
              >
                {addMutation.isPending ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Adding...
                  </span>
                ) : (
                  'Add to Department'
                )}
              </Button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};
